// Idle auto-orbit — "attract mode" for kiosk / billboard / unattended demos.
//
// After IDLE_THRESHOLD_MS of no user input (pointer / wheel / key / touch),
// slowly advances the camera's targetYaw at ORBIT_RATE_DEG_PER_SEC so the
// panorama rotates on its own. Any user input cancels orbit and resets the
// idle timer. Trade-show kiosk pattern: park a tablet, watch it sell.
//
// Disabled when XR is active (XR drives the camera directly) and when an
// edit-mode toolbar / generate panel is open (the operator is doing
// authoring work, not idle).

import type * as pc from 'playcanvas';
import type { CameraControls } from './cameraControls';

const IDLE_THRESHOLD_MS = 30_000;
const ORBIT_RATE_DEG_PER_SEC = 3;

export interface IdleOrbitDeps {
  controls: CameraControls;
  /** Returns true when orbit should NOT run (XR active, editor active, etc.) */
  isSuppressed: () => boolean;
}

export class IdleOrbit {
  private readonly app: pc.AppBase;
  private readonly controls: CameraControls;
  private readonly isSuppressed: () => boolean;
  private lastActivityMs = performance.now();
  private orbiting = false;

  constructor(app: pc.AppBase, deps: IdleOrbitDeps) {
    this.app = app;
    this.controls = deps.controls;
    this.isSuppressed = deps.isSuppressed;

    // Watch every input channel that means "user is here".
    const reset = (): void => {
      this.lastActivityMs = performance.now();
      if (this.orbiting) this.orbiting = false;
    };
    window.addEventListener('pointerdown', reset, { passive: true, capture: true });
    window.addEventListener('pointermove', reset, { passive: true, capture: true });
    window.addEventListener('wheel', reset, { passive: true, capture: true });
    window.addEventListener('keydown', reset, { capture: true });
    window.addEventListener('touchstart', reset, { passive: true, capture: true });

    this.app.on('update', this.tick);
  }

  /** External signal: tour is changing rooms / variants / sun — counts as
   *  activity so a programmatic transition doesn't trigger orbit. */
  poke(): void {
    this.lastActivityMs = performance.now();
    this.orbiting = false;
  }

  destroy(): void {
    this.app.off('update', this.tick);
  }

  private readonly tick = (dtSec: number): void => {
    if (this.isSuppressed()) {
      this.lastActivityMs = performance.now();
      this.orbiting = false;
      return;
    }
    const idleMs = performance.now() - this.lastActivityMs;
    if (idleMs < IDLE_THRESHOLD_MS) {
      this.orbiting = false;
      return;
    }
    if (!this.orbiting) this.orbiting = true;
    // Read current look so we don't fight the camera's smoothing.
    const cur = this.controls.getLook();
    const next = cur.yaw + ORBIT_RATE_DEG_PER_SEC * dtSec;
    this.controls.setLook(next, cur.pitch);
  };
}
